import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  ApproveSubcontractorRequests,
  DeclineSubcontractorRequests,
  FetchSubcontractorRequests,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import { SubcontractorRequest } from 'src/interfaces/subcontractor'
import { Pagination } from 'src/interfaces/table'

interface FetchSubcontractorRequestsPayload {
  masterContractorIds?: string[]
  contractorSearch?: string
  limit?: number
  offset?: number
  sort?: string
}

function* onFetchSubcontractorRequests(
  action: AsyncAction<
    FetchSubcontractorRequestsPayload,
    { data: SubcontractorRequest[]; pagination: Pagination }
  >,
) {
  const { masterContractorIds, contractorSearch, limit, offset, sort } = action.payload

  try {
    const query: any = {
      masterContractorIds,
      contractorSearch,
      limit,
      offset,
      sort,
    }

    const { payload }: { payload: SubcontractorRequest[] } = yield call(
      callSecureApi,
      Endpoints.FetchSubcontractorRequests,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query,
      },
    )

    const res = {
      data: payload,
      pagination: {
        total: payload.length,
        limit: payload.length,
        offset: 0,
      },
    }

    yield put(FetchSubcontractorRequests.success(res))

    if (action.next) {
      action.next(null, res)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch subcontractor requests'

    yield put(FetchSubcontractorRequests.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ApproveSubcontractorRequestsPayload {
  selectedSubcontractorRequestIds: string[]
  partnerMarketId: string
  tags?: string[]
}

function* onApproveSubcontractorRequests(action: AsyncAction<ApproveSubcontractorRequestsPayload>) {
  const { selectedSubcontractorRequestIds, partnerMarketId, tags } = action.payload

  try {
    yield call(callSecureApi, Endpoints.ApproveSubcontractorRequests, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverMasterContractorRelationIds: selectedSubcontractorRequestIds,
        partnerMarketId,
        tags,
      },
      returnResponse: true,
    })

    yield put(ApproveSubcontractorRequests.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to approve subcontractor requests'

    yield put(ApproveSubcontractorRequests.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeclineSubcontractorRequestsPayload {
  selectedSubcontractorRequestIds: string[]
}

function* onDeclineSubcontractorRequests(action: AsyncAction<DeclineSubcontractorRequestsPayload>) {
  const { selectedSubcontractorRequestIds } = action.payload

  try {
    yield call(callSecureApi, Endpoints.DeclineSubcontractorRequests, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverMasterContractorRelationIds: selectedSubcontractorRequestIds,
      },
    })

    yield put(DeclineSubcontractorRequests.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to decline subcontractor requests'

    yield put(DeclineSubcontractorRequests.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* subcontractorRequestsSaga() {
  yield takeEvery(FetchSubcontractorRequests.type.REQUEST, onFetchSubcontractorRequests)
  yield takeEvery(ApproveSubcontractorRequests.type.REQUEST, onApproveSubcontractorRequests)
  yield takeEvery(DeclineSubcontractorRequests.type.REQUEST, onDeclineSubcontractorRequests)
}
